import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';

import {Button} from 'shells/button/button';
import {Dialog} from 'shells/dialog/dialog';
import {FooterBar} from 'shells/modal/footer-bar';
import {Modal} from 'shells/modal/modal';

import type {
    CancelDripSequenceForEntity as CancelDripSequenceForEntityMutation,
    CancelDripSequenceForEntityVariables as CancelDripSequenceForEntityMutationVariables,
    CancelDripSequenceForAllEntities as CancelDripSequenceForAllEntitiesMutation,
    CancelDripSequenceForAllEntitiesVariables as CancelDripSequenceForAllEntitiesMutationVariables,
} from 'nutshell-graphql-types';

import {DripProgressionsQuery} from './drip-progressions-query';
import CANCEL_DRIP_PROGRESSION_FOR_ALL from './graphql/mutations/cancel-drip-sequence-for-all-entities.graphql';
import CANCEL_DRIP_PROGRESSION from './graphql/mutations/cancel-drip-sequence-for-entity.graphql';
import {MessageBodyPreviewModal} from './message-body-preview-modal';
import {RecipientModalList} from './recipient-modal-list';

import styles from './drip-sequence-recipient-modal.module.css';

type Props = {
    editionId: string;
    recipientCount: number;
    title: string;
    subtitle: string;
    emailTemplateId: string;
    onClose: () => void;
};

const LOAD_MORE_PIXEL_THRESHOLD = 1000;

export function DripSequenceRecipientModal(props: Props) {
    const [viewingMessage, setViewingMessage] = React.useState<any | null | undefined>(null);
    const [isConfirmingRemove, setIsConfirmingRemove] = React.useState<boolean>(false);
    const [removeAllButtonRef, setRemoveAllButtonRef] = React.useState<
        HTMLElement | null | undefined
    >(null);

    const [cancelDripProgression] = useMutation<
        CancelDripSequenceForEntityMutation,
        CancelDripSequenceForEntityMutationVariables
    >(CANCEL_DRIP_PROGRESSION);

    const [removeAllQueuedRecipients] = useMutation<
        CancelDripSequenceForAllEntitiesMutation,
        CancelDripSequenceForAllEntitiesMutationVariables
    >(CANCEL_DRIP_PROGRESSION_FOR_ALL);

    // @ts-expect-error
    const handleScroll = (e: Event, hasNextPage, isLoading, fetchMoreRows) => {
        // @ts-expect-error
        const {scrollHeight, offsetHeight, scrollTop} = e.target;

        if (
            scrollHeight - offsetHeight < scrollTop + LOAD_MORE_PIXEL_THRESHOLD &&
            hasNextPage &&
            !isLoading
        ) {
            fetchMoreRows();
        }
    };

    return (
        <DripProgressionsQuery editionId={props.editionId}>
            {({dripProgressions, isLoading, error, fetchMoreRows, hasNextPage, refetch}) => (
                <>
                    <Modal
                        onClose={props.onClose}
                        isOpen={true}
                        headerText={props.title}
                        headerSubtext={props.subtitle}
                    >
                        {isConfirmingRemove ? (
                            <Dialog
                                anchor={removeAllButtonRef}
                                question={getConfirmationText(props.recipientCount)}
                                onPrimary={() => {
                                    removeAllQueuedRecipients({
                                        variables: {
                                            deleteAllProgressionsForDripEditionInput: {
                                                editionId: props.editionId,
                                            },
                                        },
                                    })
                                        .then(() => refetch())
                                        .then(() => setIsConfirmingRemove(false));
                                }}
                                onSecondary={() => {
                                    setIsConfirmingRemove(false);
                                }}
                                primaryText='Remove'
                                secondaryText='Cancel'
                                location='top'
                            />
                        ) : null}
                        <React.Fragment>
                            <div
                                onScroll={(e) =>
                                    // @ts-expect-error
                                    handleScroll(e, hasNextPage, isLoading, fetchMoreRows)
                                }
                                className={styles['modal-scroll-container']}
                            >
                                <RecipientModalList
                                    dripProgressions={dripProgressions}
                                    onCancelDripProgression={(id) => {
                                        return cancelDripProgression({
                                            variables: {
                                                dripProgressionDeleteInput: {
                                                    dripProgressionId: id,
                                                },
                                            },
                                        }).then(() => refetch());
                                    }}
                                    emailTemplateId={props.emailTemplateId}
                                    showMessageBodyPreviewModal={(emailTemplateId, entityId) =>
                                        setViewingMessage({emailTemplateId, entityId})
                                    }
                                    isLoading={isLoading}
                                    error={error}
                                    // @ts-expect-error
                                    hasNextPage={hasNextPage}
                                    onFetchMoreRows={fetchMoreRows}
                                />
                            </div>
                            <FooterBar
                                right={
                                    <div className={styles['button-container']}>
                                        {props.recipientCount > 0 ? (
                                            <Button
                                                variant='secondary'
                                                size='big'
                                                onClick={() => setIsConfirmingRemove(true)}
                                                getButtonRef={setRemoveAllButtonRef}
                                            >
                                                {getButtonText(props.recipientCount)}
                                            </Button>
                                        ) : undefined}
                                        <Button
                                            variant='primary'
                                            size='big'
                                            onClick={props.onClose}
                                        >
                                            Done
                                        </Button>
                                    </div>
                                }
                            />
                        </React.Fragment>
                    </Modal>

                    {viewingMessage ? (
                        <MessageBodyPreviewModal
                            onClose={() => setViewingMessage(null)}
                            emailTemplateId={viewingMessage.emailTemplateId}
                            entityId={viewingMessage.entityId}
                        />
                    ) : null}
                </>
            )}
        </DripProgressionsQuery>
    );
}

const getConfirmationText = (count: number) => {
    if (count === 1) {
        return 'Are you sure you want to remove this queued recipient? This cannot be undone.';
    }

    return `Are you sure you want to remove all queued recipients? This cannot be undone.`;
};

const getButtonText = (count: number) => {
    if (count === 1) {
        return 'Remove 1 queued recipient';
    }

    return `Remove all ${count} queued recipients`;
};
